export type VendorBatchCall = {
  id?: string;
  status?: string | number;
  batchId?: string;
  batchCustomId?: string;
  pricingMode?: string;
  executionMode?: string;
  vendorBatchId?: string;
  vendorResponseId?: string;
  vendorStatus?: string;
  modelId?: string;
  resolvedModelId?: string;
  response?: string;
  error?: string;
  tokenCountPrompt?: string;
  tokenCountResponse?: string;
  cost?: number;
  createdAt?: string;
  updatedAt?: string;
};

export type GatewayBatchResponse = {
  id: string;
  object: "batch" | string;
  status: string;
  created_at: string;
  callback_secret?: string;
  request_counts: {
    total: number;
    completed: number;
    failed: number;
  };
  calls: Array<{
    custom_id: string;
    call_id: string;
    status: number;
  }>;
};

export function parseBatchPromptLines(value: string): string[] {
  return value
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
}

export function buildVendorBatchPayload(input: {
  prompts: string[];
  model: string;
  customerId: string;
  callbackUrl?: string;
  maxCompletionTokens: number;
}) {
  if (input.prompts.length === 0) {
    throw new Error("At least one prompt is required");
  }
  if (input.prompts.length > 100) {
    throw new Error("The test page supports at most 100 requests per batch");
  }
  if (!input.model) {
    throw new Error("An OpenAI model is required");
  }

  const payload: Record<string, unknown> = {
    requests: input.prompts.map((prompt, index) => ({
      custom_id: `admin-${String(index + 1).padStart(3, "0")}`,
      body: {
        model: input.model,
        stream: false,
        max_completion_tokens: input.maxCompletionTokens,
        messages: [{ role: "user", content: prompt }],
        customer: {
          customer_id: input.customerId,
          customer_name: "Admin vendor batch test",
        },
      },
    })),
  };

  if (input.callbackUrl?.trim()) {
    payload.callback_url = input.callbackUrl.trim();
  }

  return payload;
}

export function isTerminalBatchCall(call: VendorBatchCall): boolean {
  return call.status !== undefined && String(call.status) !== "202";
}

export function batchCallsComplete(calls: VendorBatchCall[], expected: number): boolean {
  return expected > 0 && calls.length >= expected && calls.every(isTerminalBatchCall);
}

export function normalizeGatewayBaseUrl(value: string): string {
  return value.trim().replace(/\/+$/, "");
}

export function createIdempotencyKey(): string {
  const uuid = globalThis.crypto?.randomUUID?.();
  return `admin-batch-${uuid || `${Date.now()}-${Math.random().toString(16).slice(2)}`}`;
}
